import { LucideIcon } from "lucide-react";
import * as React from "react";

export type IconSize = "1x" | "lg" | "2x" | number;

export interface IconProps {
	/** lucide icon component, e.g. `Trash2` from "lucide-react" */
	icon: LucideIcon;
	/** Named sizes scale with the surrounding font size; a number is pixels. Defaults to 1em. */
	size?: IconSize;
	/** Solid glyph: fill the whole icon with currentColor (e.g. Play, Star, Circle). */
	filled?: boolean;
	/** Status badge: filled circle/square container with white inner marks (e.g. CircleCheck). */
	badge?: boolean;
	spin?: boolean;
	className?: string;
	style?: React.CSSProperties;
	color?: string;
	strokeWidth?: number;
	/** Tooltip text (rendered as the SVG title attribute) */
	title?: string;
	onClick?: React.MouseEventHandler<SVGSVGElement>;
}

const namedSizes: Record<string, string> = {
	"1x": "1em",
	lg: "1.25em",
	"2x": "2em",
};

// Inline width/height style (not just attributes) is required so per-instance
// sizing beats the global `svg { width: $icon-size; ... }` rule in
// src/scss/excelerate/_icons.scss.
export const Icon: React.FC<IconProps> = ({
	icon: LucideComponent,
	size = "1x",
	filled,
	badge,
	spin,
	className,
	style,
	title,
	...rest
}) => {
	const resolved = typeof size === "number" ? size : namedSizes[size];
	const classes = [
		"lucide-inline",
		filled ? "lucide-filled" : "",
		badge ? "lucide-badge" : "",
		spin ? "icon-spin" : "",
		title ? "icon-has-title" : "",
		className ?? "",
	]
		.filter(Boolean)
		.join(" ");

	// A title attribute on <svg> gives neither a tooltip nor an accessible
	// name; a <title> child does both (as FontAwesomeIcon rendered it).
	const a11y = title ? { role: "img", "aria-hidden": false } : {};

	return (
		<LucideComponent
			className={classes}
			style={{ width: resolved, height: resolved, ...style }}
			{...a11y}
			{...rest}
		>
			{title && <title>{title}</title>}
		</LucideComponent>
	);
};
